import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import {
  multiRemoteOpsQuery,
  sendMultiRemoteCommands,
  type MultiCommandGroup,
  type MultiRemoteOpsPage,
} from '../api/opteemum'
import { toast } from '../ui/notify'
import { MultiOsPatchesTab } from './MultiOsPatches'

/**
 * Widgetul care se deschidea in portal cand bifai randuri pe o lista Opteemum.
 *
 * `opteemum/devices/details/modals.php` desena `.multiple_check_widget`
 * („Multiple devices", verde), `public/js/general.js` il arata prin
 * `MultipleActions.widgetActions()` de indata ce era bifat un rand, si prima
 * fila - Remote Actions - isi lua continutul din `Opteemum::multipleActions`
 * (`multipleActions.php`): cate un formular pentru fiecare grup de comenzi, cu
 * comenzile bifate din start, un „Inform user" si butonul cu numele grupului.
 * Trimiterea mergea in `Opteemum::remoteCommands`.
 *
 * Acelasi markup si aceleasi clase (deci aceleasi reguli din
 * `public/css/app.css`); ce difera e ca fila cere DATE si isi deseneaza
 * singura formularul, ca la `MultiActionsSidebar`.
 *
 * A doua fila, `OS Patch Management` (`multipleMissingPatches` /
 * `multipleInstalledPatches` / `multipleHiddenPatches`), e `MultiOsPatchesTab`
 * din `MultiOsPatches.tsx`. `Settings` (`multipleSettings`) n-are inca pereche
 * in v4 - nu se inventeaza una. Se adauga cand capul ei exista.
 */

function apiToast(err: unknown) {
  const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Something went wrong'
  toast(message, 'Error', 'error')
}

/**
 * Casuta desenata, ca la iCheck: portalul trecea fiecare `input` din widget
 * prin `icheckbox_flat-red`, si fara invelisul ala casuta browserului cade
 * peste eticheta. Acelasi invelis ca in `MultiActionsSidebar`.
 */
function ICheck({
  checked,
  onChange,
  name,
  value,
}: {
  checked: boolean
  onChange(on: boolean): void
  name?: string
  value?: string
}) {
  return (
    <div
      className={`icheckbox_flat-red${checked ? ' checked' : ''}`}
      style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}
    >
      <input
        type="checkbox"
        name={name}
        value={value}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, margin: 0, cursor: 'pointer' }}
      />
    </div>
  )
}

function GroupForm({ ids, group }: { ids: number[]; group: MultiCommandGroup }) {
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(group.commands.filter((c) => c.checked).map((c) => c.command))
  )
  const [inform, setInform] = useState(true)
  const [busy, setBusy] = useState(false)

  /* Selectia se schimba => lista se reincarca => bifele pornesc iar de la
     ce spune serverul, ca la fiecare `tab.trigger('click')` din portal. */
  useEffect(() => {
    setPicked(new Set(group.commands.filter((c) => c.checked).map((c) => c.command)))
  }, [group])

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    if (busy) return
    const actions = group.commands.map((c) => c.command).filter((c) => picked.has(c))
    if (actions.length === 0) {
      toast('Select at least one action.', 'Error', 'error')
      return
    }
    if (!window.confirm('Are you sure?')) return
    setBusy(true)
    try {
      const done = await sendMultiRemoteCommands(ids, group, actions, inform)
      if (done.ok) toast('Install instruction has been sent.', 'Success', 'success')
      else toast('An error occured! Please try again..', 'Error', 'error')
    } catch (err) {
      apiToast(err)
    } finally {
      setBusy(false)
    }
  }

  /* Portalul rupea lista in patru coloane de `count / 4` randuri. */
  const limit = Math.max(1, Math.round(group.commands.length / 4))
  const columns: typeof group.commands[] = []
  for (let i = 0; i < group.commands.length; i += limit) {
    columns.push(group.commands.slice(i, i + limit))
  }

  return (
    <>
      <hr />
      <div style={{ padding: '0 20px' }}>
        <form method="post" className="multiActionsForm" onSubmit={submit}>
          <div className="row">
            {columns.map((column, i) => (
              <div className="col-md-3" key={i}>
                <ul>
                  {column.map((c) => (
                    <li key={c.command} style={{ marginBottom: 10 }}>
                      <ICheck
                        name="actions[]"
                        value={c.command}
                        checked={picked.has(c.command)}
                        onChange={(on) =>
                          setPicked((s) => {
                            const next = new Set(s)
                            if (on) next.add(c.command)
                            else next.delete(c.command)
                            return next
                          })
                        }
                      />{' '}
                      {c.command_title}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20 }}>
            <button className="btn btn-primary btn-sm" type="submit" disabled={busy}>
              {group.group_name}
            </button>
            <div style={{ display: 'inline-block', verticalAlign: 'top', padding: '0 15px' }}>
              <ICheck name="inform_user" checked={inform} onChange={setInform} /> Inform user
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

function RemoteActionsTab({ ids }: { ids: number[] }) {
  const query = useQuery(multiRemoteOpsQuery(ids))

  if (query.isPending) return <div className="loading">Loading...</div>
  if (query.isError) {
    const err = query.error
    return <div className="alert alert-danger">{err instanceof Error ? err.message : 'Error'}</div>
  }
  const page = query.data as MultiRemoteOpsPage
  if (page.groups.length === 0) {
    return (
      <div className="alert alert-warning">
        No remote action applies to every selected device.
      </div>
    )
  }

  return (
    <>
      <h4>
        <strong>Multiple</strong> remote actions
      </h4>
      {page.groups.map((group) => (
        <GroupForm key={group.id} ids={ids} group={group} />
      ))}
    </>
  )
}

/**
 * Portalul: `.multiple_check_widget` cu `border-green3` / `bg-green3`, aratat
 * cat timp e bifat macar un rand. Butonul de inchis il inchide fara sa scoata
 * bifele, exact ca `hideWidgetOnClick`; urmatoarea bifa il deschide la loc.
 */
type WidgetTab = 'remote' | 'patches'

export function MultiDevicesWidget({ shown, ids, onHide }: { shown: boolean; ids: number[]; onHide(): void }) {
  const [tab, setTab] = useState<WidgetTab>('remote')
  return (
    <div className="multiple_check_widget widget border-green3" style={{ display: shown ? undefined : 'none' }}>
      <div className="widget-header bg-green3">
        <div className="widget-icon">
          <i className="fas fa-th"></i>
        </div>
        <h2 className="widget-title">Multiple devices</h2>
        <div className="widget-action">
          <button className="btn hide-widget" type="button" onClick={onHide}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div className="widget-content">
        <div style={{ width: '20%', display: 'inline-block', boxSizing: 'border-box' }}>
          <ul className="nav nav-metro-tabs tabs-left">
            <li data-class-nav="nav-content" data-toggle="tab" className={tab === 'remote' ? 'active' : undefined}>
              <a
                href="/api/v2/opteemum/devices/multi/remote-ops"
                onClick={(e) => {
                  e.preventDefault()
                  setTab('remote')
                }}
              >
                Remote Actions
              </a>
            </li>
            <li data-class-nav="nav-content" data-toggle="tab" className={tab === 'patches' ? 'active' : undefined}>
              <a
                href="/api/v2/opteemum/devices/multi/os-patches/table"
                onClick={(e) => {
                  e.preventDefault()
                  setTab('patches')
                }}
              >
                OS Patch Management
              </a>
            </li>
          </ul>
        </div>
        <div
          className="nav-content"
          style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}
        >
          {shown ? tab === 'patches' ? <MultiOsPatchesTab /> : <RemoteActionsTab ids={ids} /> : null}
        </div>
      </div>
    </div>
  )
}
